import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ArrowLeft, Star } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { CustomerPicker } from '../components/customer-picker.js';
import { Button } from '../components/ui/button.js';
import { Pill } from '../components/ui/pill.js';
import { Feedback } from '../components/ui/feedback.js';
import { QueryError } from '../components/ui/query-error.js';
import {
  api,
  type CustomerSummary,
  type InventoryItem,
  type InventoryItemSummary,
  type ResolvedPriceLine,
} from '../lib/api.js';

interface InventoryDetailSearchParams {
  customerNo?: string;
}

// localStorage key for "remember the last customer I was pricing for". The
// URL takes precedence — this only hydrates on a fresh visit with no URL
// customerNo.
const LAST_CUSTOMER_KEY = 'tinsmith:lastCustomerNo';

export function InventoryDetailScreen() {
  const { id } = useParams({ from: '/inventory/$id' });
  const search = useSearch({ from: '/inventory/$id' }) as InventoryDetailSearchParams;
  const navigate = useNavigate({ from: '/inventory/$id' });
  const spireId = Number(id);
  const customerNo = (search.customerNo ?? '').toUpperCase();

  // Hydrate customerNo from localStorage on first paint when URL has none.
  useEffect(() => {
    if (search.customerNo) return;
    const stored = window.localStorage.getItem(LAST_CUSTOMER_KEY);
    if (stored) {
      void navigate({ search: { customerNo: stored }, replace: true });
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist current selection so it sticks across navigations.
  useEffect(() => {
    if (customerNo) {
      window.localStorage.setItem(LAST_CUSTOMER_KEY, customerNo);
    }
  }, [customerNo]);

  const setCustomerNo = (next: string | undefined) => {
    void navigate({
      search: { customerNo: next && next.length > 0 ? next : undefined },
    });
  };

  const itemQuery = useQuery({
    queryKey: ['inventory', 'item', spireId],
    queryFn: ({ signal }) => api.inventory.getById(spireId, signal),
    enabled: Number.isFinite(spireId),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/inventory"
          aria-label="Back to search"
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          <span>Inventory</span>
        </Link>
        {itemQuery.data && <BookmarkToggle partNo={itemQuery.data.partNo} />}
      </div>

      {itemQuery.isError && (
        <QueryError
          title={itemQuery.error.kind === 'not_found' ? 'Item not found' : "Couldn't load item"}
          error={itemQuery.error}
          onRetry={() => void itemQuery.refetch()}
        />
      )}

      {itemQuery.isPending && <DetailSkeleton />}

      {itemQuery.data && (
        <DetailBody
          item={itemQuery.data}
          customerNo={customerNo}
          onSetCustomerNo={setCustomerNo}
        />
      )}
    </div>
  );
}

/**
 * Star toggle — persists into AppUser.bookmarkedParts via /me.
 * Optimistically patches the ['me'] cache so the icon flips immediately,
 * even before the round-trip completes.
 */
function BookmarkToggle({ partNo }: { partNo: string }) {
  const queryClient = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me.get(signal),
    staleTime: 30_000,
  });
  const me = meQuery.data;
  const bookmarked = me ? me.bookmarkedParts.includes(partNo) : false;

  const mutation = useMutation({
    mutationFn: (next: string[]) => api.me.update({ bookmarkedParts: next }),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: ['me'] });
      const previous = queryClient.getQueryData<typeof me>(['me']);
      if (previous) queryClient.setQueryData(['me'], { ...previous, bookmarkedParts: next });
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      const previous = (ctx as { previous?: typeof me } | undefined)?.previous;
      if (previous) queryClient.setQueryData(['me'], previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const toggle = () => {
    if (!me) return;
    const next = bookmarked
      ? me.bookmarkedParts.filter((p) => p !== partNo)
      : [...me.bookmarkedParts, partNo];
    mutation.mutate(next);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!me || mutation.isPending}
      aria-pressed={bookmarked}
      aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark this part'}
      className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink-accent transition-colors disabled:opacity-50"
    >
      <Star
        className="h-5 w-5"
        aria-hidden
        fill={bookmarked ? 'currentColor' : 'none'}
        style={{ color: bookmarked ? 'var(--color-text-info)' : undefined }}
      />
    </button>
  );
}

interface DetailBodyProps {
  item: InventoryItem;
  customerNo: string;
  onSetCustomerNo: (next: string | undefined) => void;
}

function DetailBody({ item, customerNo, onSetCustomerNo }: DetailBodyProps) {
  return (
    <>
      <div className="space-y-1">
        <div className="font-mono text-base font-semibold text-ink">{item.partNo}</div>
        <h1 className="text-xl font-semibold text-ink">{item.description ?? '(no description)'}</h1>
        {item.extendedDescription && (
          <p className="text-sm text-ink-muted">{item.extendedDescription}</p>
        )}
        <StatusBadge status={item.status} />
      </div>

      <CustomerPricingCard
        item={item}
        customerNo={customerNo}
        onSetCustomerNo={onSetCustomerNo}
      />

      <Card>
        <CardHeader className="flex items-center justify-between gap-2">
          <CardTitle>Stock</CardTitle>
          <span className="text-xs text-ink-subtle">Currently viewing whse <span className="font-mono text-ink-muted">{item.whseCode}</span></span>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm sm:grid-cols-4">
            <StockCell label="Available" value={`${item.availableQty} ${item.uom ?? ''}`} accent />
            <StockCell label="On hand" value={`${item.onHandQty} ${item.uom ?? ''}`} />
            <StockCell label="Committed" value={`${item.committedQty} ${item.uom ?? ''}`} />
            <StockCell label="On order" value={`${item.onPurchaseQty} ${item.uom ?? ''}`} />
          </div>

          <WarehouseStockBar
            partNo={item.partNo}
            currentWhse={item.whseCode}
            uom={item.uom}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Price ladder</CardTitle>
        </CardHeader>
        <CardBody>
          <SellPriceLadder ladder={item.sellPrices} />
        </CardBody>
      </Card>

      {item.unitOfMeasures.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Units of measure</CardTitle>
          </CardHeader>
          <CardBody>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-ink-subtle uppercase tracking-wider">
                <tr>
                  <th className="pb-2 pr-3 font-normal">Code</th>
                  <th className="pb-2 pr-3 font-normal">Description</th>
                  <th className="pb-2 text-right font-normal">Factor</th>
                </tr>
              </thead>
              <tbody>
                {item.unitOfMeasures.map((u) => (
                  <tr key={u.code} className="border-t border-line-subtle">
                    <td className="py-2 pr-3 font-mono">{u.code}</td>
                    <td className="py-2 pr-3 text-ink-muted">{u.description ?? '—'}</td>
                    <td className="py-2 text-right font-mono">{u.quantityFactor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </>
  );
}

interface CustomerPricingCardProps {
  item: InventoryItem;
  customerNo: string;
  onSetCustomerNo: (next: string | undefined) => void;
}

function CustomerPricingCard({ item, customerNo, onSetCustomerNo }: CustomerPricingCardProps) {
  const customerQuery = useQuery({
    queryKey: ['customers', 'byNo', customerNo],
    queryFn: ({ signal }) =>
      // Exact match: a substring lookup with limit 1 could resolve a
      // DIFFERENT customer whose number merely contains this one.
      api.customers.search({ customerNoExact: customerNo, limit: 5 }, signal),
    enabled: customerNo.length > 0,
    staleTime: 60_000,
  });
  const customer: CustomerSummary | undefined = customerQuery.data?.find(
    (c) => c.customerNo === customerNo,
  );
  const customerMissing =
    customerNo.length > 0 && customerQuery.isSuccess && !customer;

  // Use the customer's default warehouse if it differs from the loaded item's
  // warehouse — that's the price the rep would quote. Falls back to whichever
  // warehouse we're already viewing.
  const priceWhse = customer?.defaultWarehouse ?? item.whseCode;

  const priceQuery = useQuery<ResolvedPriceLine | undefined>({
    queryKey: ['pricing', 'resolve', customerNo, item.partNo, priceWhse],
    queryFn: async ({ signal }) => {
      const result = await api.pricing.resolve(
        {
          customerNo,
          items: [{ whse: priceWhse, partNo: item.partNo, orderQty: '1' }],
        },
        signal,
      );
      return result.lines[0];
    },
    enabled: customerNo.length > 0 && customer !== undefined,
    staleTime: 30_000,
  });

  const clear = () => {
    onSetCustomerNo(undefined);
    window.localStorage.removeItem(LAST_CUSTOMER_KEY);
  };

  if (!customerNo) {
    return (
      <Card className="border-brand-200 bg-brand-50/40">
        <CardBody>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-ink-subtle" htmlFor="cust-no-input">
              Quote for customer
            </label>
            <CustomerPicker
              id="cust-no-input"
              value=""
              placeholder="Search customer name or # to see their price"
              onSelect={(c) => onSetCustomerNo(c.customerNo)}
            />
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    // Customer-pricing card — matches Design/hvac_mobile_inventory_quote_signature.html:
    // light-blue background, accent text, big resolved price + source pill.
    <Card
      className="border-0"
      style={{ backgroundColor: '#EAF1F8' }}
    >
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="text-xs font-medium" style={{ color: '#0C447C' }}>
            For {customer ? customer.name : <span className="font-mono">{customerNo}</span>}
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-xs font-medium text-brand hover:underline focus:outline-none focus-visible:underline"
            aria-label="Clear customer"
          >
            Clear
          </button>
        </div>

        {customerQuery.isPending && <PriceSkeleton />}
        {customerMissing && (
          <Feedback variant="warning" size="sm" icon={false}>
            Customer <span className="font-mono">{customerNo}</span> not found. Check the number.
          </Feedback>
        )}
        {customer && priceQuery.isPending && <PriceSkeleton />}
        {customer && priceQuery.isError && (
          <Feedback variant="error" size="sm">
            Failed to resolve price: {priceQuery.error.message}
          </Feedback>
        )}
        {customer && priceQuery.data && (
          <ResolvedPriceDisplay
            line={priceQuery.data}
            defaultUnitPrice={item.sellPrices[0] ?? '0'}
            uom={item.uom}
            whse={priceWhse}
            customerWhseDiffers={priceWhse !== item.whseCode}
          />
        )}

        {customer && priceQuery.data?.found && (
          <Link
            to="/quote-builder"
            search={{
              customerNo,
              addWhse: priceWhse,
              addPartNo: item.partNo,
            }}
            className="block"
          >
            <Button className="w-full" aria-label="Add to a new quote for this customer">
              + Add to quote
            </Button>
          </Link>
        )}
      </CardBody>
    </Card>
  );
}

function ResolvedPriceDisplay({
  line,
  defaultUnitPrice,
  uom,
  whse,
  customerWhseDiffers,
}: {
  line: ResolvedPriceLine;
  defaultUnitPrice: string;
  uom: string | null;
  whse: string;
  customerWhseDiffers: boolean;
}) {
  if (!line.found) {
    return (
      <Feedback variant="warning" size="sm" icon={false}>
        Part not stocked at warehouse <span className="font-mono">{whse}</span>.
      </Feedback>
    );
  }
  // Show a strike-through MSRP + savings percentage when the customer's
  // resolved price is meaningfully below the item's default. This matches
  // the mockup's "$24.50 [strike $29.95] −18%" pattern.
  const resolved = Number(line.unitPrice);
  const msrp = Number(defaultUnitPrice);
  const hasDiscount =
    Number.isFinite(resolved) &&
    Number.isFinite(msrp) &&
    msrp > 0 &&
    resolved < msrp - 0.005;
  const savingsPct = hasDiscount
    ? Math.round(((msrp - resolved) / msrp) * 100)
    : null;

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold tracking-tight" style={{ color: '#042C53' }}>
          ${line.unitPrice}
        </span>
        <span className="text-xs text-ink-subtle">/ {uom ?? 'EA'}</span>
        {hasDiscount && (
          <>
            <span className="text-xs text-ink-subtle line-through">${defaultUnitPrice}</span>
            <span className="text-xs font-medium text-emerald-700">−{savingsPct}%</span>
          </>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
        <PriceSourceBadge source={line.source} sellLevel={line.sellLevel} />
        {customerWhseDiffers && (
          <span>
            at <span className="font-mono">{whse}</span> (customer default)
          </span>
        )}
      </div>
    </div>
  );
}

function PriceSourceBadge({
  source,
  sellLevel,
}: {
  source: ResolvedPriceLine['source'];
  sellLevel: number | null;
}) {
  const label =
    source === 'matrix'
      ? 'price matrix'
      : source === 'customer_sell_level'
        ? `sell level ${sellLevel ?? '?'}`
        : 'item default';
  const variant =
    source === 'matrix' ? 'matrix' : source === 'customer_sell_level' ? 'success' : 'neutral';
  return <Pill variant={variant}>{label}</Pill>;
}

function PriceSkeleton() {
  return (
    <div className="space-y-2" aria-busy="true">
      <div className="h-7 w-32 bg-surface-muted rounded animate-pulse" />
      <div className="h-3 w-24 bg-surface-muted rounded animate-pulse" />
    </div>
  );
}

/**
 * Multi-warehouse availability strip.
 *
 * Fetches all warehouse rows that carry the same partNo (Spire returns one
 * inventory record per (item, warehouse) pair, so `q=<exact partNo>` gives
 * us the cross-warehouse picture). Renders as a horizontal grid of colored
 * cells — green when stocked, amber when low, red when out.
 */
function WarehouseStockBar({
  partNo,
  currentWhse,
  uom,
}: {
  partNo: string;
  currentWhse: string;
  uom: string | null;
}) {
  const query = useQuery({
    queryKey: ['inventory', 'whse-stock', partNo],
    queryFn: ({ signal }) => api.inventory.search({ partNo, limit: 50 }, signal),
    staleTime: 30_000,
  });

  if (query.isPending) {
    return <StockBarSkeleton />;
  }
  if (query.isError) {
    return (
      <div className="text-xs text-ink-muted">
        Couldn't load per-warehouse stock: {query.error.message}
      </div>
    );
  }

  const rows = (query.data ?? [])
    .filter((r) => r.partNo.trim() === partNo)
    .sort((a, b) => a.whseCode.localeCompare(b.whseCode));

  if (rows.length === 0) return null;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-xs uppercase tracking-wider text-ink-subtle">By warehouse</div>
        {rows.length > 6 && (
          <div className="text-[10px] text-ink-subtle md:hidden">Scroll →</div>
        )}
      </div>
      {/* Mobile: horizontal scroll with snap so cells stay readable.
       * Desktop: same scroll behavior but the wider canvas usually fits
       * everything without scrolling. */}
      <div
        className="flex gap-1.5 overflow-x-auto pb-1 snap-x snap-mandatory -mx-1 px-1"
      >
        {rows.map((row) => (
          <WarehouseCell
            key={`${row.spireId}-${row.whseCode}`}
            row={row}
            uom={uom}
            isCurrent={row.whseCode === currentWhse}
          />
        ))}
      </div>
    </div>
  );
}

function WarehouseCell({
  row,
  uom,
  isCurrent,
}: {
  row: InventoryItemSummary;
  uom: string | null;
  isCurrent: boolean;
}) {
  const available = Number(row.availableQty);
  const tone =
    available <= 0
      ? { bg: 'var(--pill-emergency-bg)', fg: 'var(--pill-emergency-fg)' }
      : available <= 3
        ? { bg: 'var(--pill-sales-bg)', fg: 'var(--pill-sales-fg)' }
        : { bg: 'var(--pill-pm-bg)', fg: 'var(--pill-pm-fg)' };
  return (
    <Link
      to="/inventory/$id"
      params={{ id: String(row.spireId) }}
      className="snap-start shrink-0 flex w-[76px] flex-col items-center rounded-md px-2 py-1.5 text-center transition-shadow hover:shadow-sm"
      style={{
        backgroundColor: tone.bg,
        color: tone.fg,
        outline: isCurrent ? '2px solid var(--color-text-info)' : 'none',
        outlineOffset: '-2px',
      }}
    >
      <span className="text-[10px] font-medium uppercase tracking-wider opacity-90">
        {row.whseCode}
      </span>
      <span className="text-sm font-semibold tabular-nums">
        {row.availableQty}
      </span>
      {uom && <span className="text-[10px] opacity-75">{uom}</span>}
    </Link>
  );
}

function StockBarSkeleton() {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">By warehouse</div>
      <div className="flex gap-1.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-14 flex-1 rounded-md bg-surface-muted animate-pulse" />
        ))}
      </div>
    </div>
  );
}

function StockCell({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className={accent ? 'text-lg font-semibold text-brand' : 'text-base text-ink'}>
        {value}
      </div>
    </div>
  );
}

function SellPriceLadder({ ladder }: { ladder: string[] }) {
  const populated = ladder
    .map((price, idx) => ({ level: idx + 1, price }))
    .filter((tier) => tier.price !== '0');
  if (populated.length === 0) {
    return <div className="text-sm text-ink-subtle">No prices configured.</div>;
  }
  return (
    <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {populated.map((tier) => (
        <li key={tier.level} className="flex items-baseline justify-between gap-3">
          <span className="text-ink-muted">Level {tier.level}</span>
          <span className="font-mono">${tier.price}</span>
        </li>
      ))}
    </ul>
  );
}

function StatusBadge({ status }: { status: InventoryItem['status'] }) {
  if (status === 'active') return null;
  const variant = status === 'inactive' ? 'warning' : 'neutral';
  return <Pill variant={variant}>{status}</Pill>;
}

function DetailSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="h-5 w-24 bg-surface-muted rounded animate-pulse" />
      <div className="h-7 w-3/4 bg-surface-muted rounded animate-pulse" />
      <Card>
        <CardBody className="space-y-3">
          <div className="h-3 w-1/3 bg-surface-muted rounded animate-pulse" />
          <div className="h-5 w-1/2 bg-surface-muted rounded animate-pulse" />
        </CardBody>
      </Card>
    </div>
  );
}
